/**
 * Discoverability CTA Actions
 *
 * Feature-aware side effects for discovery prompts (beyond router.push).
 *
 * Primary responsibilities:
 * - Open in-app surfaces that plain hrefs cannot (Cmd+K, tools)
 * - Queue voice/image focus when chat composer isn't mounted yet
 * - Keep CTA labels honest without fragile query-param routes
 */

import type { DiscoverabilityFeatureKey } from './features'
import { getDiscoverabilityFeature } from './features'

export const LOSI_OPEN_WORKSPACE_SEARCH_EVENT = 'losi:open-workspace-search'
export const LOSI_ENABLE_IMAGE_GEN_EVENT = 'losi:enable-image-gen'
export const LOSI_FOCUS_VOICE_MEMO_EVENT = 'losi:focus-voice-memo'
/** Opens the Create Space dialog (listeners in sidebar + space-list). */
export const LOSI_OPEN_CREATE_SPACE_EVENT = 'losi-open-create-space'

const PENDING_ENABLE_IMAGE_GEN_KEY = 'losi:pending-enable-image-gen'
const PENDING_FOCUS_VOICE_MEMO_KEY = 'losi:pending-focus-voice-memo'

function queuePending(key: string) {
  try {
    sessionStorage.setItem(key, '1')
  } catch {
    // Private mode / blocked storage — event dispatch still helps when mounted.
  }
}

/** Chat mounts call these to drain CTAs fired before the composer existed. */
export function consumePendingEnableImageGen(): boolean {
  try {
    if (sessionStorage.getItem(PENDING_ENABLE_IMAGE_GEN_KEY) !== '1') return false
    sessionStorage.removeItem(PENDING_ENABLE_IMAGE_GEN_KEY)
    return true
  } catch {
    return false
  }
}

export function consumePendingFocusVoiceMemo(): boolean {
  try {
    if (sessionStorage.getItem(PENDING_FOCUS_VOICE_MEMO_KEY) !== '1') return false
    sessionStorage.removeItem(PENDING_FOCUS_VOICE_MEMO_KEY)
    return true
  } catch {
    return false
  }
}

export function runDiscoverabilityCta(featureKey: DiscoverabilityFeatureKey): string {
  const href = getDiscoverabilityFeature(featureKey).prompt.ctaHref

  if (typeof window === 'undefined') return href

  switch (featureKey) {
    case 'spaces':
      window.dispatchEvent(new CustomEvent(LOSI_OPEN_CREATE_SPACE_EVENT))
      return '/spaces'
    case 'connectors':
      return '/chat?sidebar=integrations'
    case 'search':
      window.dispatchEvent(new CustomEvent(LOSI_OPEN_WORKSPACE_SEARCH_EVENT))
      return '/chat'
    case 'files':
      // Open Library nav — matches label. Upload stays on the Library surface itself.
      return '/chat?sidebar=files'
    case 'images':
      queuePending(PENDING_ENABLE_IMAGE_GEN_KEY)
      window.dispatchEvent(new CustomEvent(LOSI_ENABLE_IMAGE_GEN_EVENT))
      return '/chat'
    case 'voice_memos':
      queuePending(PENDING_FOCUS_VOICE_MEMO_KEY)
      window.dispatchEvent(new CustomEvent(LOSI_FOCUS_VOICE_MEMO_EVENT))
      return '/chat'
    default:
      return href
  }
}
